import { CoverageFeeCapability } from "@redotech/shopify-client/ajax";
import { abortRace, abortTimeout } from "@redotech/util/abort";
import { log } from "../monitoring/log";

/** City Beach attaches the fee through its own Redo-*Adjustment controllers, not the Redo cartridge. */
const CITY_BEACH_HOSTNAMES = new Set([
  "dev.citybeach.com",
  "www.citybeach.com",
  "www.citybeach.com.au",
  "citybeach.com",
  "citybeach.com.au",
]);

export function isCityBeachStorefront(hostname: string): boolean {
  return CITY_BEACH_HOSTNAMES.has(hostname.toLowerCase());
}

/** A hung controller must not stall widget bootstrap (the probe is awaited before the widget mounts). */
const REQUEST_TIMEOUT = Temporal.Duration.from({ seconds: 10 });

function requestSignal(signal?: AbortSignal): AbortSignal {
  const timeout = abortTimeout(REQUEST_TIMEOUT);
  return signal ? abortRace([signal, timeout]) : timeout;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function parseSignedAmount(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string") {
    const parsed = Number(value.replace(/[^0-9.-]/g, ""));
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

const ADJUSTMENT_GROUPS = [
  "orderAdjustments",
  "productLineItemAdjustments",
  "shippingAdjustments",
] as const;
const COVERAGE_SELECTIONS = [
  ["returnsSelected", "returnsValue"],
  ["exchangeSelected", "exchangeValue"],
] as const;

/**
 * Applied Redo fee in a `Redo-GetAdjustments` body: the `redoCoverage` summary,
 * else the sum of `isRedoCoverage` adjustments. Undefined when none is applied.
 */
export function cityBeachAdjustmentAmount(body: unknown): number | undefined {
  if (!isRecord(body)) {
    return undefined;
  }
  let total: number | undefined;
  const summary = body.redoCoverage;
  if (isRecord(summary)) {
    for (const [selectedKey, valueKey] of COVERAGE_SELECTIONS) {
      const value = parseSignedAmount(summary[valueKey]);
      if (summary[selectedKey] === true && value !== undefined) {
        total = (total ?? 0) + Math.abs(value);
      }
    }
    if (total !== undefined) {
      return total;
    }
  }
  for (const group of ADJUSTMENT_GROUPS) {
    const entries = body[group];
    if (!Array.isArray(entries)) {
      continue;
    }
    for (const entry of entries) {
      if (!isRecord(entry) || entry.isRedoCoverage !== true) {
        continue;
      }
      const price = parseSignedAmount(entry.price);
      if (price !== undefined) {
        total = (total ?? 0) + Math.abs(price);
      }
    }
  }
  return total;
}

/** Add echoes `appliedValue`, Remove counts `removed`, and `error` flags a rejected call. */
interface MutationResponse {
  error: boolean;
  appliedValue?: number;
  removed?: number;
}

function parseMutationResponse(body: unknown): MutationResponse {
  if (!isRecord(body)) {
    return { error: false };
  }
  const appliedValue = parseSignedAmount(body.appliedValue);
  const removed = parseSignedAmount(body.removed);
  return {
    error: body.error === true,
    ...(appliedValue === undefined ? {} : { appliedValue }),
    ...(removed === undefined ? {} : { removed }),
  };
}

/**
 * Coverage custom fee via City Beach's `Redo-*Adjustment` controllers. The
 * probe is cached so `attachedFeeAmount()` can net the live fee out of pricing.
 */
export class CityBeachCoverageFee implements CoverageFeeCapability {
  /** Cached fee total; null when the probe failed, undefined when no fee is applied. */
  private probe?: Promise<number | undefined | null>;

  constructor(private readonly controllerBase: () => string) {}

  async isAvailable(signal?: AbortSignal): Promise<boolean> {
    return (await this.getAdjustments(signal)) !== null;
  }

  async attach(
    input: { feeAmount: number; label: string },
    signal?: AbortSignal,
  ): Promise<{ changed: boolean }> {
    const previous = await this.getAdjustments(signal);
    const params = new URLSearchParams({
      value: input.feeAmount.toFixed(2),
      description: input.label,
    });
    const result = await this.mutate(`Redo-AddAdjustment?${params}`, signal);
    if (!result) {
      return { changed: false };
    }
    const applied = result.appliedValue ?? input.feeAmount;
    this.probe = Promise.resolve(applied);
    return { changed: previous !== applied };
  }

  /** The fee is per cart, so `input.widgetSlug` (BigCommerce-only) is ignored. */
  async remove(
    _input?: { widgetSlug?: string },
    signal?: AbortSignal,
  ): Promise<{ changed: boolean }> {
    const previous = await this.getAdjustments(signal);
    const result = await this.mutate("Redo-RemoveAdjustment", signal);
    if (!result) {
      return { changed: false };
    }
    this.probe = Promise.resolve(undefined);
    return {
      changed:
        result.removed === undefined
          ? previous !== undefined && previous !== 0
          : result.removed > 0,
    };
  }

  /** City Beach redraws basket totals in place, so no page reload is needed after a change. */
  refreshAfterChange(): void {
    if (typeof globalThis.redoBasketUpdate === "function") {
      globalThis.redoBasketUpdate();
    }
  }

  /** Only reads an already-kicked-off probe, so callers that never probed see no fee. */
  async attachedFeeAmount(): Promise<number | undefined> {
    return (await this.probe) ?? undefined;
  }

  private getAdjustments(
    signal?: AbortSignal,
  ): Promise<number | undefined | null> {
    if (!this.probe) {
      this.probe = this.fetchAdjustments(signal);
    }
    return this.probe;
  }

  private async fetchAdjustments(
    signal?: AbortSignal,
  ): Promise<number | undefined | null> {
    try {
      const response = await fetch(
        `${this.controllerBase()}/Redo-GetAdjustments`,
        { signal: requestSignal(signal) },
      );
      if (!response.ok) {
        return null;
      }
      const body: unknown = await response.json().catch(() => null);
      if (body === null || (isRecord(body) && body.error === true)) {
        return null;
      }
      const amount = cityBeachAdjustmentAmount(body);
      log("Redo-GetAdjustments", { amount, body });
      return amount;
    } catch (error) {
      log("Redo-GetAdjustments request failed", error);
      return null;
    }
  }

  /** GETs a controller. Null on transport failure or when the body reports `error: true`. */
  private async mutate(
    route: string,
    signal?: AbortSignal,
  ): Promise<MutationResponse | null> {
    let result: MutationResponse;
    try {
      const response = await fetch(`${this.controllerBase()}/${route}`, {
        signal: requestSignal(signal),
      });
      if (!response.ok) {
        log(`${route} returned ${response.status}, leaving the fee unchanged`);
        return null;
      }
      result = parseMutationResponse(await response.json().catch(() => null));
    } catch (error) {
      log(`${route} request failed, leaving the fee unchanged`, error);
      return null;
    }
    if (result.error) {
      log(`${route} reported an error, leaving the fee unchanged`);
      return null;
    }
    return result;
  }
}
